import React, { useEffect, useState, useCallback } from 'react';
import { Crown, Flame } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000; // re-poll every 30s so a fresh claim drops the counter live

/**
 * FoundingCountdownTicker - scarcity ticker for the Founding tier.
 *
 * Fetches /api/billing/founding-slots on mount, then polls every 30s so the
 * number ticks down live if someone else claims a place while the visitor
 * is deciding. Renders three states:
 *   - loading (skeleton pulse)
 *   - open with N remaining (progress bar + big count + CTA scroll-to-card)
 *   - sold out (soft red state, no CTA)
 *
 * Design intent: this is the FIRST thing the visitor sees on /subscribe
 * above the pricing grid. Big number, high contrast, animated pulse when
 * fewer than 10 places are left so the scarcity actually reads.
 */
const FoundingCountdownTicker = ({ onClaimClick }) => {
  const [slots, setSlots] = useState(null);
  const [flash, setFlash] = useState(false);
  const [prevRemaining, setPrevRemaining] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/billing/founding-slots`, { cache: 'no-store' });
      if (!r.ok) return;
      const data = await r.json();
      setSlots((old) => {
        if (old && typeof old.remaining === 'number' && data.remaining < old.remaining) {
          setPrevRemaining(old.remaining);
          setFlash(true);
          setTimeout(() => setFlash(false), 1400);
        }
        return data;
      });
    } catch {
      /* preview / offline - keep last known state */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!slots) {
    return (
      <div
        data-testid="founding-ticker-loading"
        className="mb-8 rounded-lg border-2 border-yellow-500/30 bg-zinc-950/60 p-5 h-[124px] animate-pulse"
      />
    );
  }

  const { cap = 80, used = 0, remaining = 0, sold_out } = slots;
  const pct = Math.min(100, Math.max(0, Math.round((used / Math.max(cap, 1)) * 100)));
  const critical = !sold_out && remaining > 0 && remaining <= 10;

  if (sold_out) {
    return (
      <div
        data-testid="founding-ticker-sold-out"
        className="mb-8 rounded-lg border-2 border-red-700/60 bg-gradient-to-br from-red-950/40 to-black p-5 sm:p-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <Crown className="h-5 w-5 text-red-400 shrink-0" />
          <p className="text-red-300 text-[10px] font-black uppercase tracking-[0.35em]">
            Founding is closed
          </p>
        </div>
        <p className="text-white text-lg sm:text-xl font-black leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
          All {cap} Founding places are gone.
        </p>
        <p className="text-zinc-400 text-sm mt-1">
          Monthly and Annual access are still open below.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="founding-ticker"
      className={`relative overflow-hidden mb-8 rounded-lg border-2 ${
        critical
          ? 'border-red-500/80 bg-gradient-to-br from-red-950/60 via-black to-zinc-950 shadow-[0_0_36px_rgba(220,38,38,0.35)]'
          : 'border-yellow-500/70 bg-gradient-to-br from-yellow-900/25 via-black to-zinc-950 shadow-[0_0_28px_rgba(250,204,21,0.25)]'
      } p-5 sm:p-6`}
    >
      <div aria-hidden className={`pointer-events-none absolute -top-16 -right-16 h-52 w-52 rounded-full blur-3xl ${critical ? 'bg-red-600/30' : 'bg-yellow-500/20'}`} />

      <div className="relative flex items-start gap-3 mb-3">
        {critical ? (
          <Flame className="h-5 w-5 text-red-400 shrink-0 mt-0.5 animate-[pulse_1.6s_ease-in-out_infinite]" />
        ) : (
          <Crown className="h-5 w-5 text-yellow-400 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] font-black uppercase tracking-[0.35em] mb-1 flex items-center gap-2 ${critical ? 'text-red-300' : 'text-yellow-400'}`}>
            <span aria-hidden className={`inline-block h-2 w-2 rounded-full animate-pulse ${critical ? 'bg-red-400' : 'bg-yellow-400'}`} />
            {critical ? 'Almost gone' : 'Founding places live'}
          </p>
          <p className="text-white text-lg sm:text-xl font-black leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            {critical
              ? `Only ${remaining} Founding ${remaining === 1 ? 'place' : 'places'} left of ${cap}.`
              : `${remaining} Founding places left of ${cap}.`}
          </p>
          <p className="text-zinc-400 text-xs sm:text-sm mt-1">
            Lifetime access. Name on the Founders page. Never billed again.
          </p>
        </div>
      </div>

      {/* Big countdown number + progress bar */}
      <div className="relative grid grid-cols-[auto_1fr] gap-4 sm:gap-6 items-center mt-4">
        <div
          data-testid="founding-ticker-count"
          className={`relative text-center px-3 py-2 rounded border ${critical ? 'border-red-500/60 bg-black/60' : 'border-yellow-500/50 bg-black/60'} min-w-[86px]`}
        >
          <span
            className={`block text-4xl sm:text-5xl font-black tabular-nums leading-none ${critical ? 'text-red-300' : 'text-yellow-300'} ${flash ? 'animate-[ticker-flash_1.4s_ease-out]' : ''}`}
            style={{ fontFamily: 'Georgia, serif', textShadow: critical ? '0 0 14px rgba(248,113,113,0.6)' : '0 0 12px rgba(250,204,21,0.55)' }}
          >
            {remaining}
          </span>
          <span className="block text-[9px] font-black uppercase tracking-[0.25em] text-zinc-400 mt-1">
            left of {cap}
          </span>
        </div>

        <div className="min-w-0">
          <div className="w-full h-2.5 rounded-full bg-zinc-900 overflow-hidden border border-zinc-800" aria-hidden>
            <div
              className={`h-full transition-[width] duration-700 ease-out ${critical ? 'bg-gradient-to-r from-red-600 to-red-400' : 'bg-gradient-to-r from-yellow-600 to-yellow-400'}`}
              style={{ width: `${pct}%` }}
              data-testid="founding-ticker-progress"
            />
          </div>
          <div className="flex items-center justify-between mt-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-black">
            <span>{used} claimed</span>
            <span>{pct}% full</span>
          </div>

          {onClaimClick && (
            <button
              type="button"
              onClick={onClaimClick}
              data-testid="founding-ticker-cta"
              className={`mt-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded font-black text-[11px] uppercase tracking-[0.25em] transition-colors ${
                critical
                  ? 'bg-red-600 hover:bg-red-500 text-white shadow-[0_0_18px_rgba(220,38,38,0.55)]'
                  : 'bg-yellow-500 hover:bg-yellow-400 text-black'
              }`}
            >
              <Crown className="h-3.5 w-3.5" />
              Claim a Founding place
            </button>
          )}

          {flash && prevRemaining !== null && (
            <p
              className="mt-2 text-[10px] font-black uppercase tracking-[0.25em] text-red-300 animate-[ticker-pop_1.4s_ease-out]"
              data-testid="founding-ticker-flash"
              aria-live="polite"
            >
              Someone just claimed one - {prevRemaining} to {remaining}
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes ticker-flash {
          0%   { transform: scale(1);   filter: brightness(1); }
          25%  { transform: scale(1.18); filter: brightness(1.4); }
          60%  { transform: scale(0.97); }
          100% { transform: scale(1);   filter: brightness(1); }
        }
        @keyframes ticker-pop {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default FoundingCountdownTicker;
